import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Power, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';
import { classifySession, type AskMode, type LiveSuggestedAction } from '@/lib/api-client';

// Live skill triggering UI (P3 Section 4.3 + 4.4). A 30s tick feeds the last
// ~60s of transcript to Haiku; the classification surfaces 2-4 quick actions as
// softly glowing chips above the quick-action row. Clicking a chip runs the
// prompt in the indicated mode and throttles the next tick. The glow fades after
// the action is taken or 60s pass.
//
// Decision (Section 4.4): suggestions render as OVERLAY CHIPS above the existing
// fast/deep button row, not by reusing the F1-F4 buttons. The model's prompts
// are bespoke per moment, so a separate chip row is clearer than mutating the
// fixed quick-action buttons.

const TICK_MS = 30_000;
const MIN_NEW_CHARS = 50; // skip a tick if fewer than this many new chars (silence)
const WINDOW_CHARS = 1800; // ~60s of speech tail sent to the classifier
const MOMENT_TTL_MS = 60_000; // glow fades after 60s

interface LiveMoment {
  id: string;
  classification: string;
  confidence: number;
  reasoning: string;
  suggestedActions: LiveSuggestedAction[];
  receivedAtMs: number;
  timingMs: number;
  costUsd: number;
}

interface UseLiveClassifierArgs {
  sessionId: string | null;
  enabled: boolean;
  transcript: string;
}

function useLiveClassifier({ sessionId, enabled, transcript }: UseLiveClassifierArgs) {
  const [moment, setMoment] = useState<LiveMoment | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Latest values read by the interval without re-installing it each render.
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const lastLenRef = useRef(0);
  const skipNextRef = useRef(false);
  const inFlightRef = useRef(false);

  const dismiss = useCallback(() => {
    setMoment(null);
    // Throttle: skip the next tick after the operator acts, giving the model space.
    skipNextRef.current = true;
  }, []);

  // The 30s tick. Installs once; reads refs for live values.
  useEffect(() => {
    const tick = async (): Promise<void> => {
      if (inFlightRef.current || !enabledRef.current) {
        return;
      }
      const sid = sessionIdRef.current;
      if (!sid) {
        return;
      }
      if (skipNextRef.current) {
        skipNextRef.current = false;
        return;
      }
      const full = transcriptRef.current;
      if (full.length - lastLenRef.current < MIN_NEW_CHARS) {
        return; // not enough new content — treat as silence
      }
      const window = full.slice(-WINDOW_CHARS).trim();
      if (window.length === 0) {
        return;
      }

      inFlightRef.current = true;
      setClassifying(true);
      setError(null);
      try {
        const res = await classifySession(sid, window);
        lastLenRef.current = full.length;
        if (res.moment.classification !== 'UNCLEAR' && res.moment.suggestedActions.length > 0) {
          setMoment({
            ...res.moment,
            receivedAtMs: Date.now(),
            timingMs: res.timing.ms,
            costUsd: res.cost.usd,
          });
        } else {
          setMoment(null); // nothing actionable — clear any stale glow
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Classification failed');
      } finally {
        inFlightRef.current = false;
        setClassifying(false);
      }
    };

    const timer = setInterval(() => void tick(), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // Fresh baseline + cleared glow whenever the session changes.
  useEffect(() => {
    lastLenRef.current = 0;
    setMoment(null);
  }, [sessionId]);

  // Clear the glow when the operator turns the feature off.
  useEffect(() => {
    if (!enabled) {
      setMoment(null);
    }
  }, [enabled]);

  // Fade the glow after its TTL.
  useEffect(() => {
    if (!moment) {
      return;
    }
    const remaining = MOMENT_TTL_MS - (Date.now() - moment.receivedAtMs);
    const timer = setTimeout(() => setMoment(null), Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [moment]);

  return { moment, classifying, error, dismiss };
}

interface LiveSuggestionsProps {
  sessionId: string | null;
  transcript: string;
  disabled: boolean;
  onRun: (prompt: string, label: string, mode: AskMode) => void;
}

export function LiveSuggestions({ sessionId, transcript, disabled, onRun }: LiveSuggestionsProps) {
  const [enabled, setEnabled] = useState(true);
  const { moment, classifying, error, dismiss } = useLiveClassifier({ sessionId, enabled, transcript });

  if (!sessionId) {
    return null;
  }

  return (
    <div className="mb-3 rounded-md border border-stone-800/70 bg-stone-900/40 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <Sparkles className="h-3 w-3 text-amber-500" aria-hidden />
          Live cues
          {classifying ? <Loader2 className="h-3 w-3 animate-spin text-stone-500" aria-hidden /> : null}
        </span>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] transition-colors',
            enabled ? 'text-amber-400 hover:text-amber-300' : 'text-stone-600 hover:text-stone-400',
          )}
          title={enabled ? 'Live cues on — click to turn off' : 'Live cues off — click to turn on'}
        >
          <Power className="h-3 w-3" />
          {enabled ? 'On' : 'Off'}
        </button>
      </div>

      {enabled && moment && moment.suggestedActions.length > 0 ? (
        <>
          <div className="mt-2 flex flex-wrap gap-2">
            {moment.suggestedActions.map((action, i) => (
              <button
                key={`${moment.id}-${i}`}
                type="button"
                disabled={disabled}
                title={moment.reasoning}
                onClick={() => {
                  onRun(action.prompt, `live-${moment.classification.toLowerCase()}`, action.mode);
                  dismiss();
                }}
                className="live-glow inline-flex items-center gap-1.5 rounded-md border border-amber-700/50 bg-amber-950/20 px-2.5 py-1.5 text-xs text-amber-400 transition-colors hover:bg-amber-950/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="truncate">{action.label}</span>
                <span className="rounded border border-amber-800/60 px-1 py-0.5 text-[8px] uppercase tracking-[0.15em] text-amber-500/80">
                  {action.mode}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-stone-600">
            {labelForClassification(moment.classification)} · classified in{' '}
            {(moment.timingMs / 1000).toFixed(1)}s at ${moment.costUsd.toFixed(4)}
          </p>
        </>
      ) : enabled ? (
        <p className="mt-1.5 text-[10px] text-stone-600">
          {error ?? 'Listening for the next courtroom moment...'}
        </p>
      ) : null}
    </div>
  );
}

function labelForClassification(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
